import React, { useContext } from 'react';
import AppContext from 'app/AppContext';
import { usePointedTime } from 'utils/components/TimeSeriesChart/timepoint';
import { IntraDayProfileDataItem } from '../chart/IntraDayProfileData';
import IntraDayProfileLegend from './IntraDayProfileLegend';
import { deriveLegendData } from './intraDayProfileLegendData';
import IntraDayProfileHelp from 'widgets/intraDayProfile/help/IntraDayProfileHelp';
import {
    expectedVolumeTooltip,
    actualVolumeTooltip,
    cumulativeExpectedVolumeTooltip,
    expectedSpreadTooltip,
    expectedVolatilityTooltip,
    auctionTooltip,
    cumulativeActualVolumeTooltip,
} from 'widgets/intraDayProfile/help/intraDayProfileLegendTooltip';
import './IntraDayProfileLegendContainer.scss';
import classNames from 'classnames';
import { useIntradayProfileDimensions } from 'widgets/intraDayProfile/dimensions';
import { useSelectLocked } from 'layoutRenderer/store/selectors';

interface IntraDayProfileLegendContainerProps {
    dataSeries: IntraDayProfileDataItem[];
    containerLeft: number;
}

export default function IntraDayProfileLegendContainer(
    props: IntraDayProfileLegendContainerProps
) {
    const { containerLeft, dataSeries } = props;

    const { numberFormat } = useContext(AppContext);
    const time = usePointedTime('IntraDayProfileLegendContainer');
    const legendData = deriveLegendData(time, dataSeries, numberFormat);
    const { minLegendsWidth, minLegendsEditingWidth } =
        useIntradayProfileDimensions();

    const isLocked = useSelectLocked();

    const allowLegendVisibilityEditing = !isLocked;
    const cssClasses = classNames(
        'IntraDayProfileLegendContainer',
        allowLegendVisibilityEditing && 'editing-legends'
    );

    const minWidth = allowLegendVisibilityEditing
        ? minLegendsEditingWidth
        : minLegendsWidth;

    const style = { minWidth: `${minWidth}px` };

    const percentSuffix = '%';
    return (
        <div className={cssClasses} style={style}>
            <div className="legend-group">
                <IntraDayProfileLegend
                    alignDropdown={'left'}
                    className="expected LegendItem first"
                    containerLeft={containerLeft}
                    label="Exp Vol"
                    hideSeriesPropertyName="hideExpectedVolume"
                    value={legendData.expectedVolume}
                    suffix={percentSuffix}
                    tooltip={expectedVolumeTooltip}
                />
                <IntraDayProfileLegend
                    alignDropdown={'left'}
                    className="actual LegendItem last"
                    containerLeft={containerLeft}
                    label="Act Vol"
                    hideSeriesPropertyName="hideActualTradedVolume"
                    value={legendData.actualVolume}
                    suffix={percentSuffix}
                    tooltip={actualVolumeTooltip}
                />
            </div>
            <div className="legend-group">
                <IntraDayProfileLegend
                    className="cumulative-expected LegendItem first"
                    containerLeft={containerLeft}
                    label="C Exp Vol"
                    hideSeriesPropertyName="hideCumulativeExpectedVolume"
                    value={legendData.cumulativeExpectedVolume}
                    suffix={percentSuffix}
                    tooltip={cumulativeExpectedVolumeTooltip}
                />
                <IntraDayProfileLegend
                    className="cumulative-actual LegendItem last"
                    containerLeft={containerLeft}
                    label="C Act Vol"
                    hideSeriesPropertyName="hideCumulativeActualVolume"
                    value={legendData.cumulativeActualVolume}
                    suffix={percentSuffix}
                    tooltip={cumulativeActualVolumeTooltip}
                />
            </div>
            <div className="individual-legends">
                <IntraDayProfileLegend
                    className="spread"
                    containerLeft={containerLeft}
                    label="Exp Spread"
                    hideSeriesPropertyName="hideSpread"
                    value={legendData.spread}
                    suffix={'bps'}
                    tooltip={expectedSpreadTooltip}
                />
                <IntraDayProfileLegend
                    alignDropdown={'right'}
                    className="volatility"
                    containerLeft={containerLeft}
                    label="Exp Volatility"
                    hideSeriesPropertyName="hideVolatility"
                    value={legendData.volatility}
                    suffix={percentSuffix}
                    tooltip={expectedVolatilityTooltip}
                />
                <IntraDayProfileLegend
                    className="auction"
                    containerLeft={containerLeft}
                    label="Auctions"
                    hideSeriesPropertyName="hideAuctionVolume"
                    value={legendData.auctionVolume}
                    suffix={percentSuffix}
                    tooltip={auctionTooltip}
                />
                <IntraDayProfileHelp />
            </div>
        </div>
    );
}
